import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import PromptBox from './PromptBox';

const ChecklistSection = ({ data, index, total }) => {
  const [checkedItems, setCheckedItems] = useState(new Set());

  const toggleCheck = (idx) => {
    const newChecked = new Set(checkedItems);
    if (newChecked.has(idx)) {
      newChecked.delete(idx);
    } else {
      newChecked.add(idx);
    }
    setCheckedItems(newChecked);
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8 mb-8"
    >
      <div className="flex items-center gap-3 mb-4">
        <span className="text-sm font-bold text-brand-cyan bg-cyan-50 px-3 py-1 rounded-full">
          Paso {index} de {total}
        </span>
      </div>
      
      <h2 className="text-2xl font-bold text-brand-black mb-3">
        {data.title}
      </h2>
      
      <p className="text-brand-gray text-base mb-6">
        {data.explanation}
      </p>

      <div className="space-y-3 mb-8">
        {data.checkboxes.map((item, idx) => {
          const isChecked = checkedItems.has(idx);
          return (
            <label 
              key={idx} 
              className={`flex items-start gap-4 p-4 rounded-xl cursor-pointer transition-all border ${isChecked ? 'bg-cyan-50 border-brand-cyan' : 'bg-white border-gray-200 hover:border-brand-cyan'}`}
            >
              <div className="mt-0.5 relative flex-shrink-0">
                <input 
                  type="checkbox" 
                  className="sr-only"
                  checked={isChecked}
                  onChange={() => toggleCheck(idx)}
                />
                <div className={`w-6 h-6 rounded-md border flex items-center justify-center transition-colors ${isChecked ? 'bg-brand-cyan border-brand-cyan text-brand-black' : 'border-gray-300 bg-white'}`}>
                  {isChecked && <SafeIcon icon={FiIcons.FiCheck} className="text-sm" />}
                </div>
              </div>
              <span className={`text-base font-medium select-none ${isChecked ? 'text-brand-black' : 'text-brand-gray'}`}>
                {item}
              </span>
            </label>
          );
        })}
      </div>

      <PromptBox prompt={data.prompt} />

      <div className="mt-6 bg-brand-light p-5 rounded-xl border border-gray-100">
        <h4 className="text-sm font-bold text-brand-black mb-3 flex items-center gap-2">
          <SafeIcon icon={FiIcons.FiEdit3} className="text-brand-cyan" />
          Ejemplo
        </h4>
        
        {data.example.bad && data.example.good ? (
          <div className="space-y-3">
            <div>
              <span className="text-xs font-bold text-red-500 uppercase">Débil:</span>
              <p className="text-brand-gray text-sm italic mt-1">"{data.example.bad}"</p>
            </div>
            <div>
              <span className="text-xs font-bold text-green-600 uppercase">Mejor:</span>
              <p className="text-brand-black text-sm font-medium mt-1">"{data.example.good}"</p>
            </div>
          </div>
        ) : data.example.list ? (
          <ul className="space-y-2">
            {data.example.list.map((item, i) => (
              <li key={i} className="text-brand-black text-sm font-medium flex gap-2">
                <span className="text-brand-cyan">•</span> {item}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-brand-black text-sm font-medium">
            "{data.example.text}"
          </p>
        )}
      </div>
    </motion.div>
  );
};

export default ChecklistSection;